import type { YCBConfigArray } from 'Config/types';
import type { LeverKey } from 'Lib/ConfigService/types';
import type { ProtobufMessagePath } from 'Lib/utils/protobufUtils';
import type { ServiceType } from 'Services';
import {
  ACCOUNT_SERVICE,
  APPEALS_SERVICE,
  AUTH_SERVICE,
  AV_SERVICE,
  CMP_SERVICE,
  CONTACTS_SERVICE,
  DYNAMIC_UI_SERVICE,
  EXPERIMENTS_SERVICE,
  EXPLORE_SERVICE,
  FEEDBACK_SERVICE,
  GIF_SERVICE,
  INSENDIO_SERVICE,
  MEDIA_SERVICE,
  META_SERVICE,
  MM_SERVICE,
  OFFER_SERVICE,
  ONBOARDING_SERVICE,
  PASSPORT_SERVICE,
  PAYWALL_SERVICE,
  PROFILE_SERVICE,
  PURCHASE_SERVICE,
  RECS_SERVICE,
  REPORT_SERVICE,
  SPOTIFY_SERVICE,
  STACKS_UTIL_SERVICE,
  THEMES_SERVICE,
  TINDER_SELECT_SERVICE,
  TINDER_U_SERVICE,
  USER_CONNECTIONS_SERVICE,
  USER_SERVICE,
  VAULT_SERVICE,
  VENDOR_USER_TOKENS_SERVICE,
  VERIFICATION_SERVICE,
} from 'Services/constants';

// Note: declare lever const again as import const directly from the levers crushing the app
// since overrides inside the isEnabled function is not initialized at the moment
const LEVER_PURCHASE_ENABLE_NEW_PAYMENT_ENDPOINTS = 'purchase.enable_new_payment_endpoints';

export type ServiceConfig = {
  headersOverride?: Record<string, string>;
  endpoints: Record<
    string,
    {
      path: string;
      method?: string;
      // Use the replacement for the endpoint once the lever is on
      replacement?: {
        path: string;
        method?: string;
        lever: LeverKey;
      };
      requestSchema?: ProtobufMessagePath;
      responseSchema?: ProtobufMessagePath;
    }
  >;
};

export type ServicesConfig = {
  protobufServicesPath: string[];
  services: Record<ServiceType, ServiceConfig>;
};

const services: YCBConfigArray<ServicesConfig> = [
  {
    settings: ['main'] as const,
    protobufServicesPath: ['tinder', 'services'],
    services: {
      [ACCOUNT_SERVICE]: {
        endpoints: {
          connectGoogleAccount: {
            path: '/v2/account/connect/google',
            method: 'POST',
          },
          disconnectGoogleAccount: {
            path: '/v2/account/disconnect/google',
            method: 'DELETE',
          },
          connectLineAccount: {
            path: '/v2/account/connect/line',
            method: 'POST',
          },
          disconnectLineAccount: {
            path: '/v2/account/disconnect/line',
            method: 'DELETE',
          },
          dismissEmail: {
            path: '/v2/account/account-email/dismiss',
            method: 'POST',
          },
          removeEmail: {
            path: '/v2/account/account-email/remove',
            method: 'POST',
          },
          sendEmail: {
            path: '/v2/account/account-email/send',
            method: 'POST',
          },
          submitUserConsent: {
            path: '/v2/account/consents',
            method: 'POST',
          },
          verifyEmail: {
            path: '/v2/account/account-email/confirm',
            method: 'POST',
          },
        },
      },
      [APPEALS_SERVICE]: {
        endpoints: {
          activeUserGetCases: {
            path: '/v1/appeals/cases',
            method: 'GET',
          },
          bannedUserGetCases: {
            path: '/v1/banned-user/appeals/cases',
            method: 'GET',
          },
          postActiveAppeal: {
            path: '/v1/appeals',
            method: 'POST',
          },
          postBanAppeal: {
            path: '/v1/banned-user/appeals',
            method: 'POST',
          },
        },
      },
      [AUTH_SERVICE]: {
        endpoints: {
          accountRecovery: {
            path: '/v3/auth/account_recovery',
            method: 'POST',
            requestSchema: ['authgateway', 'AuthGatewayRequest'],
            responseSchema: ['authgateway', 'AuthGatewayResponse'],
          },
          appealBan: {
            path: '/challenge/verify',
            method: 'POST',
          },
          healthCheck: {
            path: '/healthcheck/auth',
            method: 'GET',
          },
          login: {
            path: '/v3/auth/login',
            method: 'POST',
            requestSchema: ['authgateway', 'AuthGatewayRequest'],
            responseSchema: ['authgateway', 'AuthGatewayResponse'],
          },
          logout: {
            path: '/v2/auth/logout',
            method: 'POST',
          },
          magicToken: {
            path: '/v4/auth/token',
            method: 'POST',
            requestSchema: ['authgateway', 'AuthGatewayRequest'],
            responseSchema: ['authgateway', 'AuthGatewayResponse'],
          },
          phoneUpdate: {
            path: '/v3/auth/phone_update',
            method: 'POST',
            requestSchema: ['authgateway', 'AuthGatewayRequest'],
            responseSchema: ['authgateway', 'AuthGatewayResponse'],
          },
          resetNotifications: {
            path: '/v2/push/notifications',
            method: 'PUT',
          },
          verifyCaptchaResponse: {
            path: '/v2/auth/verify-captcha',
            method: 'POST',
          },
        },
      },
      [AV_SERVICE]: {
        endpoints: {
          generateUrlJp: {
            path: '/verification/underage/jp/status',
            method: 'POST',
          },
          generateJumioUrl: {
            path: '/verification/underage/url',
            method: 'GET',
          },
          getJumioState: {
            path: '/verification/underage/state',
            method: 'GET',
          },
          getAnalyticsForRemoteViewJp: {
            path: '/verification/underage/jp/status',
            method: 'GET',
          },
          submitDocumentJp: {
            path: '/verification/underage/jp',
            method: 'POST',
          },
        },
      },
      [MEDIA_SERVICE]: {
        endpoints: {
          delete: {
            path: '/mediaservice',
            method: 'DELETE',
            requestSchema: ['mediaservice', 'DeleteMediaRequest'],
            responseSchema: ['mediaservice', 'ClientDataProto'],
          },
          details: {
            path: '/mediaservice/details',
            method: 'PUT',
            requestSchema: ['mediaservice', 'UpdateMediaDetailsRequest'],
            responseSchema: ['mediaservice', 'ClientDataProto'],
          },
          photo: {
            path: '/mediaservice/photo',
            method: 'POST',
          },
          placeholders: {
            path: '/mediaservice/placeholders',
            method: 'POST',
            requestSchema: ['mediaservice', 'CreatePlaceholderRequest'],
            responseSchema: ['mediaservice', 'ClientDataProto'],
          },
          order: {
            path: '/mediaservice/order',
            method: 'PUT',
            requestSchema: ['mediaservice', 'OrderMediaRequest'],
            responseSchema: ['mediaservice', 'ClientDataProto'],
          },
        },
      },
      [MM_SERVICE]: {
        endpoints: {
          benefactor: {
            path: '/v2/matchmaker/benefactor',
            method: 'GET',
          },
          recsPack: {
            path: '/v2/matchmaker/invitation',
            method: 'PATCH',
          },
          endorsement: {
            path: '/v2/matchmaker/endorsement',
            method: 'PUT',
          },
          guestRegistration: {
            path: '/v2/guest/matchmaker/registration',
            method: 'PUT',
          },
          guestBenefactor: {
            path: '/v2/guest/matchmaker/benefactor',
            method: 'GET',
          },
          guestRecsPack: {
            path: '/v2/guest/matchmaker/invitation',
            method: 'PATCH',
          },
          guestEndorsement: {
            path: '/v2/guest/matchmaker/endorsement',
            method: 'PUT',
          },
        },
      },
      [EXPERIMENTS_SERVICE]: {
        endpoints: {
          preAuthBuckets: {
            path: '/v2/buckets',
            method: 'POST',
          },
        },
      },
      [STACKS_UTIL_SERVICE]: {
        endpoints: {
          runStacksUtilCommand: {
            path: '/stacksutil/run',
            method: 'POST',
          },
        },
      },
      [INSENDIO_SERVICE]: {
        endpoints: {
          getCampaigns: {
            path: '/v2/insendio/campaigns',
            method: 'GET',
          },
          setCampaignViewed: {
            path: '/v2/insendio/campaigns/view',
            method: 'POST',
          },
        },
      },
      [META_SERVICE]: {
        endpoints: {
          postMeta: {
            path: '/v2/meta',
            method: 'POST',
          },
        },
      },
      [OFFER_SERVICE]: {
        endpoints: {
          claimOffer: {
            path: '/purchase/offer',
            method: 'POST',
          },
          setOfferSeen: {
            path: '/purchase/offer/view',
            method: 'POST',
          },
        },
      },
      [ONBOARDING_SERVICE]: {
        endpoints: {
          getOnboardingFields: {
            path: '/v2/onboarding/fields',
            method: 'GET',
          },
          updateOnboardingFields: {
            path: '/v2/onboarding/fields',
            method: 'POST',
          },
          updateOnboardingPhoto: {
            path: '/v2/onboarding/photo',
            method: 'POST',
          },
          completeOnboarding: {
            path: '/v2/onboarding/complete',
            method: 'POST',
          },
          deleteOnboarding: {
            path: '/v2/onboarding/user',
            method: 'DELETE',
          },
        },
      },
      [PASSPORT_SERVICE]: {
        endpoints: {
          fetchPopularLocations: {
            path: '/location/popular',
            method: 'GET',
          },
          submitPassportLocation: {
            path: '/passport/user/travel',
            method: 'POST',
          },
          resetPassportLocation: {
            path: '/passport/user/reset',
            method: 'POST',
          },
        },
      },
      [PAYWALL_SERVICE]: {
        endpoints: {
          getOfferPaywalls: {
            path: '/v1/paywall/offers',
            method: 'POST',
          },
        },
      },
      [PROFILE_SERVICE]: {
        endpoints: {
          authenticateInstagram: {
            path: '/instagram/authenticate',
            method: 'POST',
          },
          selectSticker: {
            path: '/v2/profile/stickers',
            method: 'POST',
          },
          getStickerCenter: {
            path: '/v2/profile/stickers',
            method: 'GET',
          },
          city: {
            path: '/v2/profile/city',
          },
          contactCard: {
            path: '/v2/profile/contact-card',
          },
          deauthorizeInstagram: {
            path: '/instagram/deauthorize',
            method: 'POST',
          },
          deleteAccount: {
            path: '/profile',
            method: 'DELETE',
          },
          deleteInterests: {
            path: '/v2/profile/userinterests',
            method: 'DELETE',
          },
          getInstagramOAuthUrl: {
            path: '/instagram/authorize',
            method: 'GET',
          },
          getUserProfile: {
            path: '/v2/profile',
          },
          setUserProfile: {
            path: '/v2/profile',
            method: 'POST',
          },
          setUser: {
            path: '/v2/profile/user',
            method: 'POST',
          },
          setUserJob: {
            path: '/v2/profile/job',
            method: 'POST',
          },
          setUserSchool: {
            path: '/v2/profile/school',
            method: 'POST',
          },
          setUserEmailSettings: {
            path: '/v2/profile/email_settings',
            method: 'POST',
          },
          setUserMutualFriendsOptInStatus: {
            path: '/v2/profile/mutuals',
            method: 'POST',
          },
          fetchEmailSubscriptions: {
            path: '/v2/subscriptions/web',
            method: 'GET',
          },
          updateEmailSubscriptions: {
            path: '/v2/subscriptions/web',
            method: 'POST',
          },
          readReceiptSubscription: {
            path: '/v2/subscriptions/readreceipts',
          },
          consumeReadReceipt: {
            path: '/v2/readreceipt/{matchId}',
          },
          media: {
            path: '/media',
          },
          startBoost: {
            path: '/boost',
            method: 'POST',
          },
          sendTutorialViewed: {
            path: '/v2/profile/tutorials',
            method: 'POST',
          },
          sendBoostResult: {
            path: '/boost/result',
            method: 'GET',
          },
          setHelpformCookies: {
            path: '/contact/request',
            method: 'POST',
          },
          submitTermsOfService: {
            path: '/v2/profile/terms-of-service',
            method: 'POST',
          },
          getPhotos: {
            path: '/profile/photos',
          },
          tinderUProfile: {
            path: '/v2/profile/tinderu',
            method: 'POST',
          },
          autocomplete: {
            path: '/v2/profile/autocomplete',
          },
          verificationSelfieStart: {
            path: '/v2/verification/selfie/start',
          },
          verificationSelfieSubmit: {
            path: '/v2/media/submit',
          },
          verificationSelfieComplete: {
            path: '/v2/verification/selfie/complete',
          },
          verificationFacemapInfo: {
            path: '/v2/verification/facemap/info',
          },
          verificationFacemapIdVerify: {
            path: '/v2/verification/facemap/id-verify',
          },
          verificationFacemapPhotoMatch: {
            path: '/v2/verification/facemap/photo-match',
          },
          username: {
            path: '/profile/username',
          },
          uploadLiveness: {
            path: '/v1/biometrics/liveness',
            method: 'POST',
          },
          uploadLivenessConsent: {
            path: '/v1/biometrics/liveness/consent',
            method: 'POST',
          },
        },
      },
      [PURCHASE_SERVICE]: {
        endpoints: {
          purchase: {
            path: '/purchase/web',
            method: 'POST',
          },
          purchase3d: {
            path: '/purchase/web/3d',
            method: 'POST',
          },
          purchaseLatest: {
            path: '/purchase/payment/latest',
          },
          purchaseGetOffer: {
            path: '/purchase/offer',
            method: 'GET',
          },
          // TODO: Replace with replacement once the new API is fully rolled out
          purchaseGetTaxDetails: {
            path: '/purchase/cc/tax/{zip}',
            method: 'POST',
            replacement: {
              path: '/v2/purchase/tax/{zip}',
              method: 'GET',
              lever: LEVER_PURCHASE_ENABLE_NEW_PAYMENT_ENDPOINTS,
            },
          },
          // TODO: Replace with replacement once the new API is fully rolled out
          purchaseEnableAutoRenew: {
            path: '/purchase/web/{purchaseId}/auto-renew',
            method: 'PUT',
            replacement: {
              path: '/v2/purchase/{purchaseId}/auto-renew',
              method: 'PUT',
              lever: LEVER_PURCHASE_ENABLE_NEW_PAYMENT_ENDPOINTS,
            },
          },
          // TODO: Replace with replacement once the new API is fully rolled out
          purchaseDisableAutoRenew: {
            path: '/purchase/web/{purchaseId}/auto-renew',
            method: 'DELETE',
            replacement: {
              path: '/v2/purchase/{purchaseId}/auto-renew',
              method: 'DELETE',
              lever: LEVER_PURCHASE_ENABLE_NEW_PAYMENT_ENDPOINTS,
            },
          },
          purchaseUpdateLatestCard: {
            path: '/purchase/payment/card',
            method: 'POST',
          },
          purchaseDeleteSavedCard: {
            path: '/purchase/payment/card',
            method: 'DELETE',
          },
          // TODO: Replace with replacement once the new API is fully rolled out
          purchaseRestore: {
            path: '/purchase/web/restore',
            method: 'POST',
            replacement: {
              path: '/v2/purchase/{restoreToken}/restore',
              method: 'PUT',
              lever: LEVER_PURCHASE_ENABLE_NEW_PAYMENT_ENDPOINTS,
            },
          },
          updateEmail: {
            path: '/purchase/web/billing/email',
            method: 'PUT',
          },
          getIncentiveEligibility: {
            path: '/v2/incentives/eligibility',
            method: 'POST',
          },
          redeemIncentive: {
            path: '/v2/incentives/redeem',
            method: 'POST',
          },
          getIncentiveIntroPriceProduct: {
            path: '/v2/incentives/introprice/product',
            method: 'POST',
          },
          redeemIncentiveIntroPrice: {
            path: '/v2/incentives/introprice/redeem',
            method: 'POST',
          },
          getGiftCardEligibility: {
            path: '/v1/incentives/gift-card/eligibility',
            method: 'POST',
          },
          redeemGiftCard: {
            path: '/v1/incentives/gift-card/redemption',
            method: 'POST',
          },
          introPriceViewed: {
            path: '/v2/purchase/discount/view',
            method: 'POST',
          },
          useBouncerBypass: {
            path: '/v2/consumables/bouncer-bypass',
            method: 'POST',
          },
          getPaymentMethods: {
            path: '/v2/purchase/payment-methods',
            method: 'GET',
          },
          getBraintreeClientToken: {
            path: '/v2/purchase/braintree/client-token',
            method: 'GET',
          },
          purchaseBraintree: {
            path: '/v2/purchase/braintree',
            method: 'POST',
          },
          purchaseAddSavedPaymentMethod: {
            path: '/v2/purchase/payment-methods/braintree/{paymentMethodType}',
            method: 'POST',
          },
          purchaseDeleteSavedPaymentMethod: {
            path: '/v2/purchase/payment-methods/braintree/{paymentMethodType}',
            method: 'DELETE',
          },
        },
      },
      [RECS_SERVICE]: {
        endpoints: {
          getRecs: {
            path: '/v2/recs/core',
          },
          recLike: {
            path: '/like/{userId}',
            method: 'POST',
          },
          recSuperlike: {
            path: '/like/{userId}/super',
            method: 'POST',
          },
          recDislike: {
            path: '/pass/{userId}',
          },
          getFastMatchRecs: {
            path: '/v2/fast-match',
            method: 'GET',
          },
          getFilteredFastMatchRecs: {
            path: '/v2/fast-match',
            method: 'POST',
          },
          fastMatchCount: {
            path: '/v2/fast-match/count',
          },
          fastMatchTeaser: {
            path: '/v2/fast-match/teaser',
          },
          fastMatchTeasers: {
            path: '/v2/fast-match/teasers',
          },
          removeSuperlike: {
            path: '/like/{userId}/super',
            method: 'DELETE',
          },
          getLikesSentRecs: {
            path: '/v2/my-likes',
          },
        },
      },
      [REPORT_SERVICE]: {
        endpoints: {
          acknowledgeReport: {
            path: '/report/ack',
            method: 'POST',
          },
          getDynamicTree: {
            path: '/v4/report/tree',
            method: 'GET',
          },
          reportUser: {
            path: '/v3/report/{primaryId}/{secondaryId}/{offenderId}',
            method: 'POST',
          },
          reportUserV4: {
            path: '/v4/report/new',
            method: 'POST',
          },
        },
      },
      [TINDER_U_SERVICE]: {
        endpoints: {
          verifyStatus: {
            path: '/v2/tinderu/verify',
            method: 'POST',
          },
        },
      },
      [USER_SERVICE]: {
        endpoints: {
          likeMessage: {
            path: '/message/{messageId}/like',
          },
          getOtherUserById: {
            path: '/user/{userId}',
          },
          getUpdates: {
            path: '/updates',
            method: 'POST',
          },
          getMatches: {
            path: '/v2/matches',
          },
          getMatch: {
            path: '/v2/matches/{matchId}',
          },
          getMatchMessages: {
            path: '/v2/matches/{matchId}/messages',
          },
          getMatchRequestVerification: {
            path: '/match-settings/selfie-verification/{matchId}',
          },
          getArchivedMatches: {
            path: '/v1/match-expiration/archived-matches',
          },
          getMessageSuggestions: {
            path: '/v2/suggestions',
          },
          sendMessage: {
            path: '/user/matches/{matchId}',
            method: 'POST',
          },
          setMatchSeen: {
            path: '/v2/seen/{matchId}',
            method: 'POST',
          },
          setLastSeenMatchMessage: {
            path: '/v2/seen/{matchId}/{messageId}',
            method: 'POST',
          },
          unmatch: {
            path: '/user/matches/{matchId}',
            method: 'DELETE',
          },
          registerDevice: {
            path: '/v2/push/devices/{deviceParam}',
            method: 'POST',
          },
          unregisterDevice: {
            path: '/v2/push/devices/{deviceParam}',
            method: 'DELETE',
          },
          getWebsocketToken: {
            path: '/ws/generate',
          },
          getAgeVerificationUrl: {
            path: '/1.0/age-verification/url',
          },
        },
      },
      [USER_CONNECTIONS_SERVICE]: {
        endpoints: {
          updateContactList: {
            path: '/v3/userconnections/contacts',
            method: 'POST',
          },
          blockUser: {
            path: '/v2/block/profile/direct',
            method: 'PUT',
          },
        },
      },
      [GIF_SERVICE]: {
        endpoints: {
          trending: {
            path: '/{type}/trending',
            method: 'GET',
          },
          search: {
            path: '/{type}/search',
            method: 'GET',
          },
        },
      },
      [SPOTIFY_SERVICE]: {
        endpoints: {
          // get spotify search results (vinyl)
          searchSpotifyTracks: {
            path: '/v2/profile/spotify/search',
            method: 'GET',
          },
          fetchPopularSongs: {
            path: '/v2/profile/spotify/popular',
            method: 'GET',
          },
          updateAnthem: {
            path: '/v2/profile/spotify/theme',
          },
          deleteAnthem: {
            path: '/v2/profile/spotify/theme',
            method: 'DELETE',
          },
          syncSpotifyAccount: {
            path: '/v2/profile/spotify/sync',
            method: 'POST',
          },
          resyncSpotifyAccount: {
            path: '/v2/profile/spotify/resync',
            method: 'POST',
          },
          updateSpotifyArtists: {
            path: '/v2/profile/spotify/artists',
            method: 'POST',
          },
          disconnectSpotify: {
            path: '/v2/profile/spotify',
            method: 'DELETE',
          },
        },
      },
      [FEEDBACK_SERVICE]: {
        endpoints: {
          sendFeedback: {
            path: '/feedback',
            method: 'POST',
          },
        },
      },
      [CMP_SERVICE]: {
        endpoints: {
          getGeoConsents: {
            path: '/v2/consents/geo',
            method: 'POST',
          },
          getUserConsents: {
            path: '/v2/profile/consents',
            method: 'POST',
          },
          updateUserConsents: {
            path: '/v2/profile/consents',
            method: 'PUT',
          },
          updateUserMessagingConsents: {
            path: '/v2/profile/consents/message',
            method: 'PUT',
          },
        },
      },
      [CONTACTS_SERVICE]: {
        endpoints: {
          getContacts: {
            path: '/v1/contacts',
            method: 'GET',
          },
          updateContacts: {
            path: '/v1/contacts',
            method: 'POST',
          },
          deleteContact: {
            path: '/v1/contacts',
            method: 'DELETE',
          },
          allContactsAction: {
            path: '/v1/contacts/all/{action}',
            method: 'POST',
          },
          getExLists: {
            path: '/v1/contacts/exclusion',
            method: 'GET',
          },
        },
      },
      // Explore Service: https://gotinder.atlassian.net/wiki/x/cAUEAg
      [EXPLORE_SERVICE]: {
        endpoints: {
          getExplore: {
            path: '/v2/explore',
            method: 'GET',
          },
          getExploreRecs: {
            path: '/v2/explore/recs',
            method: 'GET',
          },
          updateExploreOption: {
            path: '/v2/explore/options',
            method: 'PATCH',
          },
          getCampaignById: {
            path: '/v2/campaigns/{campaignId}',
            method: 'GET',
          },
        },
      },
      [THEMES_SERVICE]: {
        endpoints: {
          getTheme: {
            path: '/v2/themes/{themeId}',
            method: 'GET',
          },
        },
      },
      [TINDER_SELECT_SERVICE]: {
        endpoints: {
          selectSubscriptionOnboardingStatus: {
            path: '/v1/select-subscription-onboarding/status',
            method: 'POST',
          },
          selectSubscriptionApplication: {
            path: '/v1/select-subscription-onboarding/application',
            method: 'POST',
          },
          selectSubscriptionOnboardingComplete: {
            path: '/v1/select-subscription-onboarding/step/completion',
            method: 'POST',
          },
          selectSubscriptionCodeValidation: {
            path: '/v1/select-subscription-onboarding/code/validation',
            method: 'POST',
          },
        },
      },
      [VAULT_SERVICE]: {
        endpoints: {
          checkCode: {
            path: '/v2/tinder-vault-invitation/code/check',
            method: 'POST',
          },
          redeemCode: {
            path: '/v2/tinder-vault-invitation/redeem',
            method: 'POST',
          },
          validateCode: {
            path: '/v2/tinder-vault-invitation/validate',
            method: 'POST',
          },
          checkStatus: {
            path: '/v2/tinder-vault-invitation/status',
            method: 'GET',
          },
        },
      },
      [VENDOR_USER_TOKENS_SERVICE]: {
        endpoints: {
          generateUrl: {
            path: '/v2/vendor-user-tokens/{vendorName}/url',
            method: 'GET',
          },
        },
      },
      [VERIFICATION_SERVICE]: {
        endpoints: {
          getVerificationConsents: {
            path: '/verification/consents',
            method: 'GET',
          },
          updateVerificationConsents: {
            path: '/verification/consents',
            method: 'POST',
          },
        },
      },
      // Dynamic UI Design: https://gotinder.atlassian.net/wiki/x/31CJAQ
      [DYNAMIC_UI_SERVICE]: {
        endpoints: {
          getConfig: {
            path: '/dynamicui/configuration',
            method: 'GET',
            responseSchema: ['dynamicui', 'pages', 'Page'],
          },
          getComponentContent: {
            path: '/dynamicui/configuration/content',
            method: 'GET',
            responseSchema: ['dynamicui', 'pages', 'Page'],
          },
          getTappyContent: {
            path: '/dynamicui/tappycontent',
            method: 'GET',
            responseSchema: ['dynamicui', 'GetTappyContentResponseModel'],
          },
          getV2ComponentContent: {
            path: '/v2/dynamicui/configuration/content',
            method: 'GET',
          },
        },
      },
    },
  },
  {
    settings: ['lite:1'],
    services: {
      [PURCHASE_SERVICE]: {
        headersOverride: {
          platform: 'web',
        },
      },
    },
  },
];

export default services;
